import React, { useState, useEffect, useCallback } from "react";
import AdminPage from "../component/pages/Falcon360/admin/AdminPage";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Dashboard from "../component/pages/Falcon360/dashboard/dashboard.js";
import Header from "../component/header/Header.js";
import Sidebar from "../component/sidebar/Sidebar.js";
import CreateTable from "../component/pages/Falcon360/targetMapping/CreateTable.js";
import Footer from "../component/footer/Footer.js";
import AuditLogsTable from "../component/common/audit/AuditLogs.js";
import ImportData from "../component/pages/Falcon360/upload/ImportData.js";
import ErrorPage from "../component/common/ErrorPage.js";
import DataSource from "../component/pages/Falcon360/dataSource/DataSource.js";
import ChatInterface from "../ai-agent/ui/ChatInterface.js";
import ChatModal from "../ai-agent/ui/ChatModal.js";
import DatabaseConnectionsTable from "../component/pages/Falcon360/dataSource/DataConnections.js";
import UploadedObjectsTable from "../component/pages/Falcon360/upload/UploadedObjectsTable.js";
import HistoryData from "../component/pages/Falcon360/targetMapping/HistoryData.js";
import { LoaderCircle } from "lucide-react";
import axiosClient, { setAutoLogoutReset } from "../api-client/AxiosClient.js";
import FalconAIChatbot from "../ai-agent/ui/FalconAIChatbot.js";
import TaskManagement from "../component/pages/Todo/TaskManagement.js";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import useAutoLogout from "../hooks/useAutoLogout";
import AutoLogoutWarning from "../component/common/AutoLogoutWarning/AutoLogoutWarning";
import DataLineageDashboard from "../component/pages/Falcon360/DataLineage/datalineage.js";
import DataLineageViewer from "../component/pages/Falcon360/DataLineage/erd.js";
import DataLineageContainer from "../component/pages/Falcon360/DataLineage/DataLineageContainer.js";

const Layout = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const { instance } = useMsal();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [isRolesLoaded, setIsRolesLoaded] = useState(false);
  const [isUserGuideLoaded, setIsUserGuideLoaded] = useState(false);
  const [run, setRun] = useState(false);

  // Auto-logout handlers
  const handleAutoLogout = useCallback(() => {
    instance.logout().catch((e) => {
      console.error(e);
    });
    toast.warning("Your session has expired due to inactivity. Please login again.");
  }, [instance]);

  const handleManualLogout = useCallback(() => {
    instance.logout().catch((e) => {
      console.error(e);
    });
  }, [instance]);

  // Use auto-logout hook (15 minutes inactivity, 30 seconds warning)
  const { showWarning, remainingTime, resetTimer } = useAutoLogout(
    handleAutoLogout,
    120 * 60 * 1000, // 15 minutes
    30 * 1000 // 30 seconds warning before logout
  );

  // Register resetTimer with AxiosClient for API call tracking
  useEffect(() => {
    setAutoLogoutReset(resetTimer);
    return () => {
      setAutoLogoutReset(null);
    };
  }, [resetTimer]);

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  const isAgentRoute = location.pathname === "/ai-agent";
  const isViewDataRoute = location.pathname === "/view-data";
  const isHistoryPage = location.pathname.includes("/history-data");
  // Define valid paths for authenticated users
  const validPaths = [
    "/dashboard",
    "/upload",
    "/mapping",
    "/view-data",
    "/ai-agent",
    "/profile",
    "/falcon-ai",
    "/data-sources",
    "/data-sources/add-data-source",
    "/upload/add-upload",
    "/history-data",
    "/admin-interface",
    "/task",
    "/data-lineage",
    "/erd",
    // Add all other valid authenticated routes here
  ];

  const fetchisUserGuideViewed = async () => {
    const response = await axiosClient.get("api/User/GuideViewStatus");
    setRun(response.data || false);
    setIsUserGuideLoaded(true);
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axiosClient.get("/api/User/Permissions");
        if (response && response.data) {
          const merged = {};

          Object.values(response.data).forEach((role) => {
            Object.entries(role).forEach(([module, permissions]) => {
              if (!merged[module]) {
                merged[module] = { ...permissions };
              } else {
                Object.keys(permissions).forEach((key) => {
                  merged[module][key] = merged[module][key] || permissions[key];
                });
              }
            });
          });
          setTimeout(() => {
            setIsRolesLoaded(true);
          }, 1000);
          sessionStorage.setItem("rolesAndPermission", JSON.stringify(merged));
        }
      } catch (err) {
        console.log(err);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    fetchisUserGuideViewed();
  }, []);

  // Check if current path is valid
  const isValidPath =
    validPaths.includes(location.pathname) ||
    location.pathname.startsWith("/history-data/");

  // If path is invalid, redirect to dashboard
  if (!isValidPath && location.pathname !== "/") {
    return <Navigate to="/dashboard" replace />;
  }

  const closeChat = () => setChatOpen(false);
  const toggleMinimize = () => setChatMinimized((prev) => !prev);

  // Calculate content width based on route and sidebar state
  const getContentStyle = () => {
    const baseStyle = {
      marginLeft: !isAgentRoute ? (isOpen ? "0px" : "80px") : "0px",
      marginTop: "80px", // Add margin-top to account for fixed header
      padding: !isAgentRoute ? "10px" : "0px",
      width: "100%",
      transition: "all 0.3s ease-in-out",
    };

    if (isHistoryPage) {
      return {
        ...baseStyle,
        overflow: "hidden",
        marginLeft: isOpen ? "0px" : "95px",
      };
    }

    // Special handling for view-data route (CreateTable component)
    if (isViewDataRoute) {
      return {
        ...baseStyle,
        maxWidth: isOpen ? "calc(100vw)" : "calc(100vw - 50px)",
        overflow: "hidden",
        marginLeft: isOpen ? "0px" : "95px",
      };
    }

    return baseStyle;
  };

  return (
    <>
      {isRolesLoaded && isUserGuideLoaded ? (
        <div
          className="app-wrapper"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Header
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            setRun={setRun}
            run={run}
          />

          <div
            className={`app-container ${isAgentRoute ? "h-screen" : ""}`}
            style={{ display: "flex", flex: 1 }}
          >
            {!isAgentRoute && <Sidebar isOpen={isOpen} />}

            <div className="content" style={getContentStyle()}>
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route
                  path="/data-sources"
                  element={<DatabaseConnectionsTable />}
                />
                <Route path="/upload" element={<UploadedObjectsTable />} />
                <Route path="/view-data" element={<CreateTable />} />
                <Route path="/logs" element={<AuditLogsTable />} />
                <Route path="/error" element={<ErrorPage />} />
                <Route path="/ai-agent" element={<ChatInterface />} />
                <Route path="/history-data/:id" element={<HistoryData />} />
                <Route path="/falcon-ai" element={<FalconAIChatbot />} />
                <Route path="/task" element={<TaskManagement />} />
                <Route path="/erd" element={<DataLineageDashboard />} />
                <Route path="/erd"element={<DataLineageContainer/>}/>
                <Route
                  path="/data-sources/add-data-source"
                  element={<DataSource />}
                />
                <Route path="/upload/add-upload" element={<ImportData />} />
                {/* Catch all invalid paths and redirect to dashboard */}
                <Route
                  path="*"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route path="/admin-interface" element={<AdminPage />} />
              </Routes>
            </div>
            {/* Floating Chat Icon */}
            {/* {!isAgentRoute && (
              <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end">
                <div className="relative bg-blue-900 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-md animate-pulse">
                  Ask Me...
                  <div className="absolute bottom-[-8px] right-3 w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-blue-800"></div>
                </div>

                <div
                  onClick={openChat}
                  className="mt-2 w-14 h-14 cursor-pointer rounded-full border-2 border-blue-900 bg-white shadow-lg hover:shadow-xl transition-shadow animate-pulse overflow-hidden"
                >
                  <img
                    src={BotIcon}
                    alt="Chat Bot"
                    className="p-2 object-cover w-full h-full"
                  />
                </div>
              </div>
            )} */}

            {/* Chat Modal */}
            <ChatModal
              open={chatOpen}
              minimized={chatMinimized}
              onClose={closeChat}
              onMinimize={toggleMinimize}
            >
              <ChatInterface />
            </ChatModal>
          </div>

          <Footer />
        </div>
      ) : (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
          <LoaderCircle className="animate-spin text-blue-900" size={50} />
        </div>
      )}
      {/* Auto-logout Warning Modal */}
      <AutoLogoutWarning
        show={showWarning}
        remainingTime={remainingTime}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleManualLogout}
      />
    </>
  );
};

export default Layout;
